/** Group types that appear as sections in the generation list. */
const SECTION_TYPES = new Set(["section", "section_agentic"]);

interface SectionGroup {
  id: string;
  type?: string | null;
  [key: string]: unknown;
}

/**
 * Section list for a report opened from a Report Run download.
 *
 * The download only carries the sections that were generated, so the frozen
 * catalog keeps those exactly as downloaded. Template sections the download
 * lacks (failed or never generated in the run) are added from the live
 * template as ordinary, not-yet-generated sections: the document still holds
 * their `_START`/`_END` bookmarks, so they can be generated here. Placeholder
 * (BASE) groups are never added, and with no live template (offline) only the
 * frozen sections remain.
 */
export function buildDownloadedSectionList<T extends SectionGroup>(
  frozenCatalog: readonly T[],
  liveGroups: readonly SectionGroup[],
): Array<T | SectionGroup> {
  const liveById = new Map(liveGroups.map((group) => [group.id, group]));
  const frozenIds = new Set(frozenCatalog.map((frozen) => frozen.id));
  const frozenSections = frozenCatalog.map((frozen) => ({
    ...liveById.get(frozen.id),
    ...frozen,
  }));
  const notGenerated = liveGroups.filter(
    (group) =>
      !frozenIds.has(group.id) &&
      SECTION_TYPES.has(String(group.type ?? "").toLowerCase()),
  );
  return [...frozenSections, ...notGenerated];
}
